<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canAdd = computed(() => store.getters.hasPermission("expense.add"));
const canEdit = computed(() => store.getters.hasPermission("expense.edit"));
const canDelete = computed(() => store.getters.hasPermission("expense.delete"));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="ec-head">
          <router-link to="/expenselist" class="ec-back" aria-label="Back to expenses"><i class="fas fa-arrow-left"></i></router-link>
          <div>
            <h3>Expense categories</h3>
            <p>Group your expenses — Rent, Salary, Electricity, Transport... — to see where money goes.</p>
          </div>
        </div>

        <!-- Quick add -->
        <div v-if="canAdd" class="ec-card ec-add">
          <div class="ec-add-row">
            <div class="ec-add-name">
              <i class="fas fa-plus-circle"></i>
              <input
                ref="nameInput"
                v-model.trim="newCategory.categoryname"
                type="text"
                maxlength="100"
                placeholder="New category name, e.g. Electricity"
                @keydown.enter.prevent="addCategory"
              />
            </div>
            <input v-model.trim="newCategory.description" type="text" class="ec-add-desc" maxlength="250" placeholder="Description (optional)" @keydown.enter.prevent="addCategory" />
            <button type="button" class="ec-btn-primary" :disabled="adding || !newCategory.categoryname" @click="addCategory">
              <i class="fas" :class="adding ? 'fa-spinner fa-spin' : 'fa-plus'"></i> Add
            </button>
          </div>
          <div v-if="!categories.length && !loading" class="ec-suggest">
            <span>Quick start:</span>
            <button v-for="name in suggestions" :key="name" type="button" class="ec-chip" @click="newCategory.categoryname = name; addCategory()">{{ name }}</button>
          </div>
        </div>

        <div class="ec-toolbar">
          <div class="ec-search">
            <i class="fas fa-search"></i>
            <input v-model.trim="search" type="search" placeholder="Search categories..." />
          </div>
          <div class="ec-tabs">
            <button v-for="tab in tabs" :key="tab.value" type="button" :class="{ on: show === tab.value }" @click="show = tab.value">
              {{ tab.label }} <span>{{ tab.count }}</span>
            </button>
          </div>
          <span class="ec-month">Spend shown for {{ monthName }}</span>
        </div>

        <div v-if="loading" class="ec-card ec-empty"><i class="fas fa-spinner fa-spin"></i> Loading...</div>
        <div v-else-if="!visible.length" class="ec-card ec-empty">
          <div class="ec-empty-icon"><i class="fas fa-tags"></i></div>
          <strong>{{ categories.length ? "No categories match" : "No expense categories yet" }}</strong>
          <span v-if="!categories.length">Add your first category above to start recording expenses.</span>
        </div>
        <div v-else class="ec-grid">
          <div v-for="category in visible" :key="category.categoryid" class="ec-card ec-item" :class="{ inactive: !category.is_active, editing: editingId === category.categoryid }">
            <template v-if="editingId === category.categoryid">
              <label class="ec-label">Name</label>
              <input v-model.trim="editForm.categoryname" type="text" class="ec-input" maxlength="100" @keydown.enter.prevent="saveEdit" @keydown.esc="editingId = null" />
              <label class="ec-label">Description</label>
              <input v-model.trim="editForm.description" type="text" class="ec-input" maxlength="250" placeholder="Optional" @keydown.enter.prevent="saveEdit" @keydown.esc="editingId = null" />
              <div class="ec-edit-actions">
                <button type="button" class="ec-btn-light" @click="editingId = null">Cancel</button>
                <button type="button" class="ec-btn-primary" :disabled="savingId === category.categoryid" @click="saveEdit">Save</button>
              </div>
            </template>
            <template v-else>
              <div class="ec-item-top">
                <span class="ec-swatch" :style="{ background: categoryColor(category.categoryid) }"><i class="fas fa-tag"></i></span>
                <div class="ec-item-title">
                  <strong>{{ category.categoryname }}</strong>
                  <small>{{ category.description || "No description" }}</small>
                </div>
                <label v-if="canEdit" class="ec-switch" :title="category.is_active ? 'Active — click to hide from new expenses' : 'Inactive — click to use again'">
                  <input type="checkbox" :checked="category.is_active" :disabled="savingId === category.categoryid" @change="toggleActive(category)" />
                  <span></span>
                </label>
              </div>
              <div class="ec-stats">
                <div>
                  <span>This month</span>
                  <strong>{{ money(statOf(category.categoryid).amount) }}</strong>
                </div>
                <div>
                  <span>Entries</span>
                  <strong>{{ statOf(category.categoryid).count }}</strong>
                </div>
                <div>
                  <span>Share</span>
                  <strong>{{ share(category.categoryid) }}%</strong>
                </div>
              </div>
              <div class="ec-bar"><span :style="{ width: `${share(category.categoryid)}%`, background: categoryColor(category.categoryid) }"></span></div>
              <div class="ec-item-foot">
                <span class="ec-status" :class="category.is_active ? 'on' : 'off'">{{ category.is_active ? "Active" : "Inactive" }}</span>
                <router-link :to="{ path: '/expenselist', query: { categoryid: category.categoryid } }" class="ec-link">View expenses</router-link>
                <div class="ec-item-actions">
                  <button v-if="canEdit" type="button" class="ec-icon" title="Edit" aria-label="Edit" @click="startEdit(category)"><i class="fas fa-pen"></i></button>
                  <button v-if="canDelete" type="button" class="ec-icon danger" title="Delete" aria-label="Delete" @click="deleteCategory(category)"><i class="fas fa-trash-alt"></i></button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { categoryColor, isoDate } from "@/views/pages/expense/expenseShared";

export default {
  name: "expensecategory",
  data() {
    return {
      categories: [],
      stats: {},
      monthTotal: 0,
      loading: false,
      adding: false,
      savingId: null,
      editingId: null,
      editForm: { categoryname: "", description: "" },
      newCategory: { categoryname: "", description: "" },
      search: "",
      show: "all",
      suggestions: ["Rent", "Salary", "Electricity", "Transport", "Internet & Phone", "Repairs", "Tea & Snacks", "Office supplies"],
    };
  },
  computed: {
    monthName() {
      return new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    },
    tabs() {
      const active = this.categories.filter((c) => c.is_active).length;
      return [
        { value: "all", label: "All", count: this.categories.length },
        { value: "active", label: "Active", count: active },
        { value: "inactive", label: "Inactive", count: this.categories.length - active },
      ];
    },
    visible() {
      const term = this.search.toLowerCase();
      return this.categories
        .filter((c) => (this.show === "active" ? c.is_active : this.show === "inactive" ? !c.is_active : true))
        .filter((c) => !term || c.categoryname.toLowerCase().includes(term) || (c.description || "").toLowerCase().includes(term))
        .sort((a, b) => this.statOf(b.categoryid).amount - this.statOf(a.categoryid).amount || a.categoryname.localeCompare(b.categoryname));
    },
  },
  methods: {
    categoryColor,
    money(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    },
    statOf(id) {
      return this.stats[id] || { amount: 0, count: 0 };
    },
    share(id) {
      if (!this.monthTotal) return 0;
      return Math.round((this.statOf(id).amount / this.monthTotal) * 100);
    },
    async load() {
      this.loading = true;
      try {
        const now = new Date();
        const [categories, expenses] = await Promise.all([
          axios.get("/v1/expenses/categories", { params: { include_inactive: true } }),
          axios.get("/v1/expenses", { params: { date_from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: isoDate(now) } }).catch(() => ({ data: [] })),
        ]);
        this.categories = categories.data || [];
        const stats = {};
        let total = 0;
        (expenses.data || []).forEach((e) => {
          const row = stats[e.categoryid] || { amount: 0, count: 0 };
          row.amount += Number(e.amount || 0);
          row.count += 1;
          stats[e.categoryid] = row;
          total += Number(e.amount || 0);
        });
        this.stats = stats;
        this.monthTotal = total;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load categories", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    async addCategory() {
      const name = this.newCategory.categoryname;
      if (!name || this.adding) return;
      if (this.categories.some((c) => c.categoryname.toLowerCase() === name.toLowerCase())) {
        Swal.fire({ toast: true, position: "top-end", icon: "info", title: `"${name}" already exists`, showConfirmButton: false, timer: 1800 });
        return;
      }
      this.adding = true;
      try {
        await axios.post("/v1/expenses/categories", { categoryname: name, description: this.newCategory.description || null, is_active: true });
        this.newCategory = { categoryname: "", description: "" };
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Added "${name}"`, showConfirmButton: false, timer: 1500 });
        await this.load();
        this.$nextTick(() => this.$refs.nameInput?.focus());
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not add category", text: error.response?.data?.detail || error.message });
      } finally {
        this.adding = false;
      }
    },
    startEdit(category) {
      this.editingId = category.categoryid;
      this.editForm = { categoryname: category.categoryname, description: category.description || "" };
    },
    async saveEdit() {
      const category = this.categories.find((c) => c.categoryid === this.editingId);
      if (!category || !this.editForm.categoryname) return;
      this.savingId = category.categoryid;
      try {
        await axios.put(`/v1/expenses/categories/${category.categoryid}`, {
          categoryname: this.editForm.categoryname,
          description: this.editForm.description || null,
          is_active: category.is_active,
        });
        this.editingId = null;
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Category updated", showConfirmButton: false, timer: 1400 });
        await this.load();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not update", text: error.response?.data?.detail || error.message });
      } finally {
        this.savingId = null;
      }
    },
    async toggleActive(category) {
      this.savingId = category.categoryid;
      try {
        await axios.put(`/v1/expenses/categories/${category.categoryid}`, {
          categoryname: category.categoryname,
          description: category.description || null,
          is_active: !category.is_active,
        });
        category.is_active = !category.is_active;
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: category.is_active ? "Category active" : "Category hidden from new expenses", showConfirmButton: false, timer: 1500 });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not update", text: error.response?.data?.detail || error.message });
        await this.load();
      } finally {
        this.savingId = null;
      }
    },
    async deleteCategory(category) {
      const result = await Swal.fire({
        icon: "warning",
        title: `Delete "${category.categoryname}"?`,
        text: "Categories already used by expenses can't be deleted — switch them to inactive instead.",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        confirmButtonColor: "#dc2626",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/v1/expenses/categories/${category.categoryid}`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Category deleted", showConfirmButton: false, timer: 1400 });
        await this.load();
      } catch (error) {
        const detail = error.response?.data?.detail || error.message;
        const used = /used by expenses/i.test(detail);
        const next = await Swal.fire({
          icon: used ? "info" : "error",
          title: used ? "This category is in use" : "Could not delete",
          text: used ? "It has expenses recorded against it. Make it inactive to hide it from new expenses?" : detail,
          showCancelButton: used && category.is_active,
          confirmButtonText: used && category.is_active ? "Make inactive" : "OK",
        });
        if (used && category.is_active && next.isConfirmed) await this.toggleActive(category);
      }
    },
  },
  mounted() {
    this.load();
    this.$nextTick(() => this.$refs.nameInput?.focus());
  },
};
</script>

<style scoped>
.ec-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.ec-head h3 { margin: 0; font-size: 20px; font-weight: 700; color: #111827; }
.ec-head p { margin: 2px 0 0; font-size: 13px; color: #6b7280; }
.ec-back { width: 38px; height: 38px; flex: 0 0 38px; border-radius: 10px; border: 1px solid #e5e7eb; background: #fff; display: flex; align-items: center; justify-content: center; color: #374151; }
.ec-back:hover { border-color: #FF9F43; color: #c2410c; }
.ec-card { background: #fff; border: 1px solid #e9ecf2; border-radius: 14px; }
.ec-add { padding: 12px; margin-bottom: 14px; }
.ec-add-row { display: flex; gap: 10px; flex-wrap: wrap; }
.ec-add-name { position: relative; flex: 1 1 260px; }
.ec-add-name i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #FF9F43; font-size: 16px; }
.ec-add-name input, .ec-add-desc { width: 100%; height: 46px; border: 1.5px solid #e5e7eb; border-radius: 12px; padding: 0 14px; font-size: 14.5px; background: #fafafa; }
.ec-add-name input { padding-left: 42px; font-weight: 600; }
.ec-add-desc { flex: 1 1 240px; width: auto; }
.ec-add-name input:focus, .ec-add-desc:focus { outline: none; border-color: #FF9F43; background: #fff; }
.ec-btn-primary { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 46px; padding: 0 22px; border: 0; border-radius: 12px; background: #FF9F43; color: #fff; font-weight: 600; font-size: 14px; }
.ec-btn-primary:disabled { opacity: 0.55; }
.ec-btn-light { height: 38px; padding: 0 16px; border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; color: #374151; font-weight: 600; font-size: 13px; }
.ec-edit-actions .ec-btn-primary { height: 38px; padding: 0 16px; border-radius: 10px; font-size: 13px; }
.ec-suggest { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 10px; font-size: 13px; color: #6b7280; }
.ec-chip { border: 1px dashed #fdba74; background: #fffaf5; color: #c2410c; border-radius: 999px; padding: 4px 12px; font-size: 12.5px; font-weight: 600; }

.ec-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.ec-search { position: relative; flex: 0 1 300px; }
.ec-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.ec-search input { width: 100%; height: 38px; padding-left: 34px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 13.5px; }
.ec-tabs { display: inline-flex; padding: 3px; border-radius: 10px; background: #f3f4f6; }
.ec-tabs button { border: 0; background: transparent; padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; color: #6b7280; }
.ec-tabs button span { margin-left: 4px; padding: 0 7px; border-radius: 999px; background: #e5e7eb; font-size: 11px; }
.ec-tabs button.on { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.ec-month { margin-left: auto; font-size: 12.5px; color: #9ca3af; }

.ec-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 40px 16px; color: #6b7280; font-size: 14px; text-align: center; }
.ec-empty strong { color: #111827; font-size: 16px; }
.ec-empty-icon { width: 54px; height: 54px; border-radius: 16px; background: #fff4e8; color: #FF9F43; display: flex; align-items: center; justify-content: center; font-size: 22px; }

.ec-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
.ec-item { padding: 16px; display: flex; flex-direction: column; gap: 12px; transition: box-shadow 0.15s ease, border-color 0.15s ease; }
.ec-item:hover { box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06); }
.ec-item.inactive { background: #fafafa; }
.ec-item.inactive .ec-item-title strong { color: #6b7280; }
.ec-item.editing { border-color: #FF9F43; box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.12); gap: 6px; }
.ec-item-top { display: flex; align-items: center; gap: 12px; }
.ec-swatch { width: 40px; height: 40px; flex: 0 0 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; }
.ec-item.inactive .ec-swatch { filter: grayscale(1); opacity: 0.6; }
.ec-item-title { flex: 1; min-width: 0; }
.ec-item-title strong { display: block; font-size: 15px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ec-item-title small { display: block; font-size: 12.5px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ec-stats { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 8px; }
.ec-stats div { display: flex; flex-direction: column; }
.ec-stats span { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #9ca3af; }
.ec-stats strong { font-size: 15px; color: #111827; font-variant-numeric: tabular-nums; }
.ec-bar { height: 6px; border-radius: 999px; background: #f1f2f4; overflow: hidden; }
.ec-bar span { display: block; height: 100%; border-radius: 999px; }
.ec-item-foot { display: flex; align-items: center; gap: 10px; }
.ec-status { font-size: 11.5px; font-weight: 600; padding: 2px 9px; border-radius: 999px; }
.ec-status.on { background: #dcfce7; color: #15803d; }
.ec-status.off { background: #f3f4f6; color: #6b7280; }
.ec-link { font-size: 12.5px; color: #c2410c; font-weight: 600; }
.ec-item-actions { margin-left: auto; display: flex; gap: 4px; }
.ec-icon { width: 32px; height: 32px; border: 0; border-radius: 8px; background: #f3f4f6; color: #4b5563; font-size: 12px; }
.ec-icon:hover { background: #fff4e8; color: #c2410c; }
.ec-icon.danger:hover { background: #fef2f2; color: #dc2626; }
.ec-label { font-size: 12px; font-weight: 600; color: #6b7280; margin: 4px 0 0; }
.ec-input { height: 38px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 0 12px; font-size: 14px; }
.ec-input:focus { outline: none; border-color: #FF9F43; }
.ec-edit-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }

.ec-switch { position: relative; width: 40px; height: 22px; flex: 0 0 40px; margin: 0; cursor: pointer; }
.ec-switch input { opacity: 0; width: 0; height: 0; }
.ec-switch span { position: absolute; inset: 0; border-radius: 999px; background: #d1d5db; transition: background 0.15s ease; }
.ec-switch span::after { content: ""; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); transition: transform 0.15s ease; }
.ec-switch input:checked + span { background: #22c55e; }
.ec-switch input:checked + span::after { transform: translateX(18px); }
.ec-switch input:focus-visible + span { box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.3); }

@media (max-width: 576px) {
  .ec-month { margin-left: 0; width: 100%; }
}
</style>
